import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import {
    TrendingUp,
    TrendingDown,
    AlertTriangle,
    CheckCircle,
    Info,
    Target,
    BarChart3,
    Calendar,
} from 'lucide-react'
import dayjs from 'dayjs'
import { ProductGroupReportItem, ServiceMonthlyReportItem } from '../types'

interface ServiceAnalysisProps {
    monthlyData: ServiceMonthlyReportItem[]
    previousYearMonthlyData?: ServiceMonthlyReportItem[]
    productGroupData?: ProductGroupReportItem[]
    previousYearProductGroupData?: ProductGroupReportItem[]
    year: number
    showComparison: boolean
}

// Key product groups to focus on
const KEY_PRODUCT_GROUPS = {
    CDN: ['CONTENT DELIVERY NETWORK', 'CDN'],
    VNIS_WAF: ['VNIS', 'WAF V2', 'WAFV2', 'WAF'],
}

type InsightType = 'positive' | 'negative' | 'warning' | 'info'

interface Insight {
    type: InsightType
    title: string
    description: string
    metric?: string
}

const ServiceAnalysis: React.FC<ServiceAnalysisProps> = ({
    monthlyData,
    previousYearMonthlyData,
    productGroupData,
    previousYearProductGroupData,
    year,
    showComparison,
}) => {
    const { t } = useTranslation()

    // Helper to match product group names
    const matchProductGroup = (name: string, patterns: string[]): boolean => {
        const upperName = name.toUpperCase()
        return patterns.some((p) => upperName.includes(p) || upperName === p)
    }

    // Calculate yearly totals
    const yearlyStats = useMemo(() => {
        if (!monthlyData || monthlyData.length === 0) return null

        const totalNew = monthlyData.reduce((s, d) => s + d.new_services, 0)
        const totalCancelled = monthlyData.reduce(
            (s, d) => s + d.cancelled_services,
            0
        )
        const totalCancelledAmount = monthlyData.reduce(
            (s, d) => s + d.cancelled_amount,
            0
        )
        const firstMonth = monthlyData[0]
        const lastMonth = monthlyData[monthlyData.length - 1]
        const netChange =
            lastMonth.ending_services - firstMonth.starting_services
        const avgCancellationRate =
            monthlyData.reduce((s, d) => s + d.cancellation_rate, 0) /
            monthlyData.length
        const avgRetentionRate =
            monthlyData.reduce((s, d) => s + d.retention_rate, 0) /
            monthlyData.length

        return {
            totalNew,
            totalCancelled,
            totalCancelledAmount,
            netChange,
            avgCancellationRate,
            avgRetentionRate,
            startServices: firstMonth.starting_services,
            endServices: lastMonth.ending_services,
            growthRate:
                firstMonth.starting_services > 0
                    ? ((lastMonth.ending_services -
                          firstMonth.starting_services) /
                          firstMonth.starting_services) *
                      100
                    : 0,
        }
    }, [monthlyData])

    // Previous year stats
    const prevYearStats = useMemo(() => {
        if (!previousYearMonthlyData || previousYearMonthlyData.length === 0)
            return null

        const totalNew = previousYearMonthlyData.reduce(
            (s, d) => s + d.new_services,
            0
        )
        const totalCancelled = previousYearMonthlyData.reduce(
            (s, d) => s + d.cancelled_services,
            0
        )
        const totalCancelledAmount = previousYearMonthlyData.reduce(
            (s, d) => s + d.cancelled_amount,
            0
        )
        const firstMonth = previousYearMonthlyData[0]
        const lastMonth =
            previousYearMonthlyData[previousYearMonthlyData.length - 1]
        const avgCancellationRate =
            previousYearMonthlyData.reduce(
                (s, d) => s + d.cancellation_rate,
                0
            ) / previousYearMonthlyData.length

        return {
            totalNew,
            totalCancelled,
            totalCancelledAmount,
            startServices: firstMonth.starting_services,
            endServices: lastMonth.ending_services,
            avgCancellationRate,
        }
    }, [previousYearMonthlyData])

    // Monthly variation analysis
    const monthlyVariation = useMemo(() => {
        if (!monthlyData || monthlyData.length < 2) return null

        // Find best and worst months
        const sortedByNew = [...monthlyData].sort(
            (a, b) => b.new_services - a.new_services
        )
        const sortedByCancelled = [...monthlyData].sort(
            (a, b) => b.cancelled_services - a.cancelled_services
        )
        const sortedByRate = [...monthlyData].sort(
            (a, b) => b.cancellation_rate - a.cancellation_rate
        )

        // Month-over-month changes
        const momChanges = monthlyData.slice(1).map((curr, idx) => {
            const prev = monthlyData[idx]
            return {
                month: curr.month,
                newChange: curr.new_services - prev.new_services,
                cancelledChange:
                    curr.cancelled_services - prev.cancelled_services,
                endingChange: curr.ending_services - prev.ending_services,
            }
        })

        // Find significant drops/increases
        const significantDrops = momChanges.filter(
            (m) => m.endingChange < -10 || m.cancelledChange > 5
        )
        const significantGains = momChanges.filter(
            (m) => m.endingChange > 10 || m.newChange > 5
        )

        return {
            bestNewMonth: sortedByNew[0],
            worstNewMonth: sortedByNew[sortedByNew.length - 1],
            highestCancelMonth: sortedByCancelled[0],
            lowestCancelMonth: sortedByCancelled[sortedByCancelled.length - 1],
            highestRateMonth: sortedByRate[0],
            lowestRateMonth: sortedByRate[sortedByRate.length - 1],
            significantDrops,
            significantGains,
        }
    }, [monthlyData])

    // Key product group analysis (CDN & VNIS+WAF)
    const keyProductGroupsAnalysis = useMemo(() => {
        if (!productGroupData) return null

        // Find CDN data
        const cdnData = productGroupData.find((p) =>
            matchProductGroup(p.product_group_name, KEY_PRODUCT_GROUPS.CDN)
        )
        const prevCdnData = previousYearProductGroupData?.find((p) =>
            matchProductGroup(p.product_group_name, KEY_PRODUCT_GROUPS.CDN)
        )

        // Find VNIS + WAF data (combine them)
        const vnisWafData = productGroupData.filter((p) =>
            matchProductGroup(p.product_group_name, KEY_PRODUCT_GROUPS.VNIS_WAF)
        )
        const prevVnisWafData = previousYearProductGroupData?.filter((p) =>
            matchProductGroup(p.product_group_name, KEY_PRODUCT_GROUPS.VNIS_WAF)
        )

        // Combine VNIS + WAF stats
        const combinedVnisWaf =
            vnisWafData.length > 0
                ? {
                      total_services: vnisWafData.reduce(
                          (s, d) => s + d.total_services,
                          0
                      ),
                      new_services: vnisWafData.reduce(
                          (s, d) => s + d.new_services,
                          0
                      ),
                      cancelled_services: vnisWafData.reduce(
                          (s, d) => s + d.cancelled_services,
                          0
                      ),
                      lost_revenue: vnisWafData.reduce(
                          (s, d) => s + d.lost_revenue,
                          0
                      ),
                      retention_rate:
                          vnisWafData.reduce(
                              (s, d) => s + d.retention_rate,
                              0
                          ) / vnisWafData.length,
                      names: vnisWafData.map((d) => d.product_group_name),
                  }
                : null

        const prevCombinedVnisWaf =
            prevVnisWafData && prevVnisWafData.length > 0
                ? {
                      total_services: prevVnisWafData.reduce(
                          (s, d) => s + d.total_services,
                          0
                      ),
                      new_services: prevVnisWafData.reduce(
                          (s, d) => s + d.new_services,
                          0
                      ),
                      cancelled_services: prevVnisWafData.reduce(
                          (s, d) => s + d.cancelled_services,
                          0
                      ),
                  }
                : null

        return {
            cdn: cdnData,
            prevCdn: prevCdnData,
            vnisWaf: combinedVnisWaf,
            prevVnisWaf: prevCombinedVnisWaf,
        }
    }, [productGroupData, previousYearProductGroupData])

    // Generate insights
    const insights = useMemo(() => {
        const result: Insight[] = []

        if (!yearlyStats) return result

        // Yearly overview insights
        if (yearlyStats.netChange > 0) {
            result.push({
                type: 'positive',
                title: t('reports.analysis.net_growth'),
                description: t('reports.analysis.net_growth_desc', {
                    count: yearlyStats.netChange,
                    rate: yearlyStats.growthRate.toFixed(1),
                    year,
                }),
                metric: `+${yearlyStats.netChange}`,
            })
        } else if (yearlyStats.netChange < 0) {
            result.push({
                type: 'negative',
                title: t('reports.analysis.net_decline'),
                description: t('reports.analysis.net_decline_desc', {
                    count: Math.abs(yearlyStats.netChange),
                    rate: Math.abs(yearlyStats.growthRate).toFixed(1),
                    year,
                }),
                metric: `${yearlyStats.netChange}`,
            })
        }

        // YoY comparison
        if (showComparison && prevYearStats) {
            const newGrowth =
                prevYearStats.totalNew > 0
                    ? ((yearlyStats.totalNew - prevYearStats.totalNew) /
                          prevYearStats.totalNew) *
                      100
                    : 0
            const cancelledChange =
                yearlyStats.totalCancelled - prevYearStats.totalCancelled

            if (newGrowth > 10) {
                result.push({
                    type: 'positive',
                    title: t('reports.analysis.new_services_up'),
                    description: t('reports.analysis.new_services_up_desc', {
                        rate: newGrowth.toFixed(1),
                        current: yearlyStats.totalNew,
                        previous: prevYearStats.totalNew,
                    }),
                    metric: `+${newGrowth.toFixed(0)}%`,
                })
            } else if (newGrowth < -10) {
                result.push({
                    type: 'warning',
                    title: t('reports.analysis.new_services_down'),
                    description: t('reports.analysis.new_services_down_desc', {
                        rate: Math.abs(newGrowth).toFixed(1),
                        current: yearlyStats.totalNew,
                        previous: prevYearStats.totalNew,
                    }),
                    metric: `${newGrowth.toFixed(0)}%`,
                })
            }

            if (cancelledChange < -5) {
                result.push({
                    type: 'positive',
                    title: t('reports.analysis.cancellation_improved'),
                    description: t(
                        'reports.analysis.cancellation_improved_desc',
                        {
                            count: Math.abs(cancelledChange),
                            current: yearlyStats.totalCancelled,
                            previous: prevYearStats.totalCancelled,
                        }
                    ),
                    metric: `${cancelledChange}`,
                })
            } else if (cancelledChange > 5) {
                result.push({
                    type: 'negative',
                    title: t('reports.analysis.cancellation_increased'),
                    description: t(
                        'reports.analysis.cancellation_increased_desc',
                        {
                            count: cancelledChange,
                            current: yearlyStats.totalCancelled,
                            previous: prevYearStats.totalCancelled,
                        }
                    ),
                    metric: `+${cancelledChange}`,
                })
            }
        }

        // Retention rate insight
        if (yearlyStats.avgRetentionRate >= 95) {
            result.push({
                type: 'positive',
                title: t('reports.analysis.excellent_retention'),
                description: t('reports.analysis.excellent_retention_desc', {
                    rate: yearlyStats.avgRetentionRate.toFixed(1),
                }),
                metric: `${yearlyStats.avgRetentionRate.toFixed(1)}%`,
            })
        } else if (yearlyStats.avgRetentionRate < 90) {
            result.push({
                type: 'warning',
                title: t('reports.analysis.retention_needs_attention'),
                description: t(
                    'reports.analysis.retention_needs_attention_desc',
                    {
                        rate: yearlyStats.avgRetentionRate.toFixed(1),
                    }
                ),
                metric: `${yearlyStats.avgRetentionRate.toFixed(1)}%`,
            })
        }

        // Monthly variation insights
        if (monthlyVariation) {
            if (monthlyVariation.significantDrops.length > 0) {
                const dropMonths = monthlyVariation.significantDrops
                    .map((d) => dayjs(d.month + '-01').format('MMM'))
                    .join(', ')
                result.push({
                    type: 'warning',
                    title: t('reports.analysis.significant_drops'),
                    description: t('reports.analysis.significant_drops_desc', {
                        months: dropMonths,
                    }),
                })
            }

            if (monthlyVariation.highestCancelMonth.cancelled_services > 10) {
                result.push({
                    type: 'info',
                    title: t('reports.analysis.peak_cancellation_month'),
                    description: t(
                        'reports.analysis.peak_cancellation_month_desc',
                        {
                            month: dayjs(
                                monthlyVariation.highestCancelMonth.month +
                                    '-01'
                            ).format('MMMM'),
                            count: monthlyVariation.highestCancelMonth
                                .cancelled_services,
                            rate: monthlyVariation.highestCancelMonth.cancellation_rate.toFixed(
                                1
                            ),
                        }
                    ),
                    metric: `${monthlyVariation.highestCancelMonth.cancelled_services}`,
                })
            }
        }

        // Key product group insights
        if (keyProductGroupsAnalysis) {
            // CDN Analysis
            if (keyProductGroupsAnalysis.cdn) {
                const cdn = keyProductGroupsAnalysis.cdn
                if (cdn.retention_rate >= 95) {
                    result.push({
                        type: 'positive',
                        title: t('reports.analysis.cdn_strong'),
                        description: t('reports.analysis.cdn_strong_desc', {
                            services: cdn.total_services,
                            retention: cdn.retention_rate.toFixed(1),
                        }),
                        metric: `${cdn.retention_rate.toFixed(1)}%`,
                    })
                } else if (cdn.cancelled_services > 5) {
                    result.push({
                        type: 'warning',
                        title: t('reports.analysis.cdn_attention'),
                        description: t('reports.analysis.cdn_attention_desc', {
                            cancelled: cdn.cancelled_services,
                            lost: (cdn.lost_revenue / 1000000).toFixed(1),
                        }),
                        metric: `-${cdn.cancelled_services}`,
                    })
                }
            }

            // VNIS + WAF Analysis
            if (keyProductGroupsAnalysis.vnisWaf) {
                const vw = keyProductGroupsAnalysis.vnisWaf
                if (vw.new_services > 10) {
                    result.push({
                        type: 'positive',
                        title: t('reports.analysis.vnis_waf_growth'),
                        description: t(
                            'reports.analysis.vnis_waf_growth_desc',
                            {
                                new: vw.new_services,
                                total: vw.total_services,
                            }
                        ),
                        metric: `+${vw.new_services}`,
                    })
                }
                if (vw.cancelled_services > 3) {
                    result.push({
                        type: 'info',
                        title: t('reports.analysis.vnis_waf_churn'),
                        description: t('reports.analysis.vnis_waf_churn_desc', {
                            cancelled: vw.cancelled_services,
                            retention: vw.retention_rate.toFixed(1),
                        }),
                        metric: `-${vw.cancelled_services}`,
                    })
                }
            }
        }

        return result
    }, [
        yearlyStats,
        prevYearStats,
        monthlyVariation,
        keyProductGroupsAnalysis,
        showComparison,
        year,
        t,
    ])

    const getInsightIcon = (type: InsightType) => {
        switch (type) {
            case 'positive':
                return <CheckCircle size={20} className="text-green-600" />
            case 'negative':
                return <TrendingDown size={20} className="text-red-600" />
            case 'warning':
                return <AlertTriangle size={20} className="text-amber-600" />
            default:
                return <Info size={20} className="text-blue-600" />
        }
    }

    const getInsightBgColor = (type: InsightType) => {
        switch (type) {
            case 'positive':
                return 'bg-green-50 border-green-200'
            case 'negative':
                return 'bg-red-50 border-red-200'
            case 'warning':
                return 'bg-amber-50 border-amber-200'
            default:
                return 'bg-blue-50 border-blue-200'
        }
    }

    if (!yearlyStats) return null

    return (
        <div className="space-y-4">
            {/* Yearly Overview */}
            <div className="bg-white rounded-md p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                    <BarChart3 size={20} className="text-primary" />
                    <Typogaphy className="text-lg font-semibold text-gray-800">
                        {t('reports.analysis.yearly_overview')} {year}
                    </Typogaphy>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <Typogaphy className="text-2xl font-bold text-gray-800">
                            {yearlyStats.endServices.toLocaleString()}
                        </Typogaphy>
                        <Typogaphy className="text-xs text-gray-500">
                            {t('reports.analysis.total_active_services')}
                        </Typogaphy>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                        <Typogaphy className="text-2xl font-bold text-green-600">
                            +{yearlyStats.totalNew.toLocaleString()}
                        </Typogaphy>
                        <Typogaphy className="text-xs text-gray-500">
                            {t('reports.analysis.new_this_year')}
                        </Typogaphy>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                        <Typogaphy className="text-2xl font-bold text-red-600">
                            -{yearlyStats.totalCancelled.toLocaleString()}
                        </Typogaphy>
                        <Typogaphy className="text-xs text-gray-500">
                            {t('reports.analysis.cancelled_this_year')}
                        </Typogaphy>
                    </div>
                    <div
                        className={`text-center p-3 rounded-lg ${yearlyStats.netChange >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}
                    >
                        <Typogaphy
                            className={`text-2xl font-bold ${yearlyStats.netChange >= 0 ? 'text-blue-600' : 'text-orange-600'}`}
                        >
                            {yearlyStats.netChange >= 0 ? '+' : ''}
                            {yearlyStats.netChange.toLocaleString()}
                        </Typogaphy>
                        <Typogaphy className="text-xs text-gray-500">
                            {t('reports.analysis.net_change')}
                        </Typogaphy>
                    </div>
                </div>

                {/* Key Product Groups Focus */}
                {keyProductGroupsAnalysis && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-2 mb-3">
                            <Target size={16} className="text-primary" />
                            <Typogaphy className="text-sm font-semibold text-gray-700">
                                {t('reports.analysis.key_products_focus')}
                            </Typogaphy>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {/* CDN */}
                            {keyProductGroupsAnalysis.cdn && (
                                <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                                    <Typogaphy className="font-semibold text-cyan-800">
                                        CDN
                                    </Typogaphy>
                                    <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                                        <div>
                                            <span className="text-gray-600">
                                                {t('total')}:
                                            </span>{' '}
                                            <span className="font-medium">
                                                {
                                                    keyProductGroupsAnalysis.cdn
                                                        .total_services
                                                }
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-green-600">
                                                +
                                                {
                                                    keyProductGroupsAnalysis.cdn
                                                        .new_services
                                                }
                                            </span>{' '}
                                            <span className="text-gray-500">
                                                {t('new').toLowerCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-red-600">
                                                -
                                                {
                                                    keyProductGroupsAnalysis.cdn
                                                        .cancelled_services
                                                }
                                            </span>{' '}
                                            <span className="text-gray-500">
                                                {t('cancelled').toLowerCase()}
                                            </span>
                                        </div>
                                    </div>
                                    <Typogaphy className="text-xs text-cyan-700 mt-1">
                                        {t('retention_rate')}:{' '}
                                        {keyProductGroupsAnalysis.cdn.retention_rate.toFixed(
                                            1
                                        )}
                                        %
                                    </Typogaphy>
                                </div>
                            )}

                            {/* VNIS + WAF */}
                            {keyProductGroupsAnalysis.vnisWaf && (
                                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                                    <Typogaphy className="font-semibold text-purple-800">
                                        VNIS + WAF V2
                                    </Typogaphy>
                                    <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                                        <div>
                                            <span className="text-gray-600">
                                                {t('total')}:
                                            </span>{' '}
                                            <span className="font-medium">
                                                {
                                                    keyProductGroupsAnalysis
                                                        .vnisWaf.total_services
                                                }
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-green-600">
                                                +
                                                {
                                                    keyProductGroupsAnalysis
                                                        .vnisWaf.new_services
                                                }
                                            </span>{' '}
                                            <span className="text-gray-500">
                                                {t('new').toLowerCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-red-600">
                                                -
                                                {
                                                    keyProductGroupsAnalysis
                                                        .vnisWaf
                                                        .cancelled_services
                                                }
                                            </span>{' '}
                                            <span className="text-gray-500">
                                                {t('cancelled').toLowerCase()}
                                            </span>
                                        </div>
                                    </div>
                                    <Typogaphy className="text-xs text-purple-700 mt-1">
                                        {t('retention_rate')}:{' '}
                                        {keyProductGroupsAnalysis.vnisWaf.retention_rate.toFixed(
                                            1
                                        )}
                                        %
                                    </Typogaphy>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Monthly Variation */}
            {monthlyVariation && (
                <div className="bg-white rounded-md p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2 mb-4">
                        <Calendar size={20} className="text-primary" />
                        <Typogaphy className="text-lg font-semibold text-gray-800">
                            {t('reports.analysis.monthly_variation')}
                        </Typogaphy>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="p-3 bg-green-50 rounded-lg text-center">
                            <Typogaphy className="text-xs text-gray-500 mb-1">
                                {t('reports.analysis.best_new_month')}
                            </Typogaphy>
                            <Typogaphy className="font-semibold text-green-700">
                                {dayjs(
                                    monthlyVariation.bestNewMonth.month + '-01'
                                ).format('MMM')}
                            </Typogaphy>
                            <Typogaphy className="text-sm text-green-600">
                                +{monthlyVariation.bestNewMonth.new_services}
                            </Typogaphy>
                        </div>
                        <div className="p-3 bg-red-50 rounded-lg text-center">
                            <Typogaphy className="text-xs text-gray-500 mb-1">
                                {t('reports.analysis.highest_cancel_month')}
                            </Typogaphy>
                            <Typogaphy className="font-semibold text-red-700">
                                {dayjs(
                                    monthlyVariation.highestCancelMonth.month +
                                        '-01'
                                ).format('MMM')}
                            </Typogaphy>
                            <Typogaphy className="text-sm text-red-600">
                                -
                                {
                                    monthlyVariation.highestCancelMonth
                                        .cancelled_services
                                }
                            </Typogaphy>
                        </div>
                        <div className="p-3 bg-amber-50 rounded-lg text-center">
                            <Typogaphy className="text-xs text-gray-500 mb-1">
                                {t('reports.analysis.highest_rate_month')}
                            </Typogaphy>
                            <Typogaphy className="font-semibold text-amber-700">
                                {dayjs(
                                    monthlyVariation.highestRateMonth.month +
                                        '-01'
                                ).format('MMM')}
                            </Typogaphy>
                            <Typogaphy className="text-sm text-amber-600">
                                {monthlyVariation.highestRateMonth.cancellation_rate.toFixed(
                                    1
                                )}
                                %
                            </Typogaphy>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg text-center">
                            <Typogaphy className="text-xs text-gray-500 mb-1">
                                {t('reports.analysis.lowest_rate_month')}
                            </Typogaphy>
                            <Typogaphy className="font-semibold text-blue-700">
                                {dayjs(
                                    monthlyVariation.lowestRateMonth.month +
                                        '-01'
                                ).format('MMM')}
                            </Typogaphy>
                            <Typogaphy className="text-sm text-blue-600">
                                {monthlyVariation.lowestRateMonth.cancellation_rate.toFixed(
                                    1
                                )}
                                %
                            </Typogaphy>
                        </div>
                    </div>
                </div>
            )}

            {/* Insights */}
            {insights.length > 0 && (
                <div className="bg-white rounded-md p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2 mb-4">
                        <TrendingUp size={20} className="text-primary" />
                        <Typogaphy className="text-lg font-semibold text-gray-800">
                            {t('reports.analysis.key_insights')}
                        </Typogaphy>
                    </div>

                    <div className="space-y-3">
                        {insights.map((insight, idx) => (
                            <div
                                key={idx}
                                className={`p-3 rounded-lg border ${getInsightBgColor(insight.type)}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0 mt-0.5">
                                        {getInsightIcon(insight.type)}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <Typogaphy className="font-semibold text-gray-800">
                                                {insight.title}
                                            </Typogaphy>
                                            {insight.metric && (
                                                <Typogaphy
                                                    className={`font-bold text-lg ${
                                                        insight.type ===
                                                        'positive'
                                                            ? 'text-green-600'
                                                            : insight.type ===
                                                                'negative'
                                                              ? 'text-red-600'
                                                              : insight.type ===
                                                                  'warning'
                                                                ? 'text-amber-600'
                                                                : 'text-blue-600'
                                                    }`}
                                                >
                                                    {insight.metric}
                                                </Typogaphy>
                                            )}
                                        </div>
                                        <Typogaphy className="text-sm text-gray-600 mt-1">
                                            {insight.description}
                                        </Typogaphy>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ServiceAnalysis
